import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings, X } from 'lucide-react'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from './LocalizedLink'

const hoverChampagne =
  'hover:bg-[color-mix(in_srgb,var(--color-highlight)_28%,var(--color-bg))] hover:text-[var(--color-fg)]'

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      {/* Banner — editorial dossier, bottom-left */}
      {!showPreferences && (
        <div
          className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 w-[calc(100vw-32px)] sm:w-[420px] max-w-[420px] bg-[var(--color-surface)] overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-300"
          style={{
            zIndex: 9999,
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-xl)',
            border: '1px solid var(--color-border)',
          }}
          role="dialog"
          aria-label={t('cookie.title', 'Cookie consent')}
        >
          {/* Header — ink, eyebrow + serif title — same vocabulary as chatbot */}
          <div
            className="relative px-6 pt-5 pb-5"
            style={{
              background: 'var(--color-accent)',
              color: 'var(--color-on-accent)',
            }}
          >
            <span
              className="inline-flex items-center gap-3"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-caption)',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-eyebrow)',
                color: 'var(--color-highlight)',
              }}
            >
              {t('cookie.eyebrow', { defaultValue: 'Privacy & Cookie' })}
              <span
                aria-hidden="true"
                className="inline-block"
                style={{
                  width: '2rem',
                  height: '1px',
                  background: 'var(--color-highlight)',
                  opacity: 0.7,
                }}
              />
            </span>
            <h3
              className="mt-2 text-[1.5rem] leading-[1.1]"
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 500,
                letterSpacing: 'var(--tracking-display)',
                color: 'var(--color-on-accent)',
              }}
            >
              {t('cookie.title', { defaultValue: 'Un attimo di consenso' })}
            </h3>
            <span
              aria-hidden="true"
              className="absolute left-0 right-0 bottom-0 h-px"
              style={{
                background:
                  'linear-gradient(90deg, transparent, var(--color-highlight) 20%, var(--color-highlight) 80%, transparent)',
                opacity: 0.55,
              }}
            />
          </div>

          {/* Body */}
          <div
            className="px-6 py-5"
            style={{ background: 'var(--color-bg)' }}
          >
            <p
              className="text-[0.875rem] mb-5"
              style={{
                fontFamily: 'var(--font-body)',
                color: 'var(--color-fg)',
                lineHeight: 1.6,
              }}
            >
              {t('cookie.message')}{' '}
              <LocalizedLink
                to="privacy"
                className="underline underline-offset-4 transition-colors hover:opacity-80"
                style={{
                  color: 'var(--color-fg)',
                  textDecorationColor: 'var(--color-highlight)',
                  textDecorationThickness: '1px',
                }}
              >
                {t('cookie.learnMore')}
              </LocalizedLink>
            </p>

            <div className="flex flex-row gap-2">
              <button
                onClick={acceptAll}
                className={`flex-1 h-11 px-4 text-sm font-medium border transition-[background,color,border] duration-[var(--duration-fast)] bg-[var(--color-accent)] text-[var(--color-on-accent)] border-[var(--color-accent)] ${hoverChampagne} hover:border-[var(--color-fg)]`}
                style={{
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {t('cookie.acceptAll', 'Accetta tutti')}
              </button>
              <button
                onClick={rejectAll}
                className={`flex-1 h-11 px-4 text-sm font-medium border transition-[background,color,border] duration-[var(--duration-fast)] bg-transparent text-[var(--color-fg)] border-[var(--color-fg)] ${hoverChampagne}`}
                style={{
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {t('cookie.rejectAll', 'Rifiuta')}
              </button>
            </div>

            <button
              onClick={() => setShowPreferences(true)}
              className="mt-4 flex items-center justify-center gap-1.5 w-full transition-colors duration-[var(--duration-fast)] hover:text-[var(--color-fg)]"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.625rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--color-muted)',
                fontWeight: 500,
              }}
            >
              <span
                aria-hidden="true"
                className="inline-block"
                style={{ width: '14px', height: '1px', background: 'var(--color-highlight)' }}
              />
              <Settings className="h-3 w-3" strokeWidth={1.5} />
              {t('cookie.customize', 'Personalizza')}
              <span
                aria-hidden="true"
                className="inline-block"
                style={{ width: '14px', height: '1px', background: 'var(--color-highlight)' }}
              />
            </button>
          </div>
        </div>
      )}

      {/* Preferences Modal — same editorial vocabulary */}
      {showPreferences && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 animate-in fade-in duration-200"
          style={{
            zIndex: 10000,
            background: 'color-mix(in srgb, var(--color-fg) 70%, transparent)',
          }}
          onClick={() => setShowPreferences(false)}
        >
          <div
            className="bg-[var(--color-surface)] max-w-[460px] w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 border-2 border-[var(--color-fg)]"
            style={{
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow-xl)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="relative px-6 pt-5 pb-5 flex items-start justify-between gap-4"
              style={{
                background: 'var(--color-accent)',
                color: 'var(--color-on-accent)',
              }}
            >
              <div className="min-w-0">
                <span
                  className="inline-flex items-center gap-3"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-caption)',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-eyebrow)',
                    color: 'var(--color-highlight)',
                  }}
                >
                  {t('cookie.eyebrow', { defaultValue: 'Privacy & Cookie' })}
                  <span
                    aria-hidden="true"
                    className="inline-block"
                    style={{
                      width: '2rem',
                      height: '1px',
                      background: 'var(--color-highlight)',
                      opacity: 0.7,
                    }}
                  />
                </span>
                <h2
                  className="mt-2 text-[1.5rem] leading-[1.1]"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 500,
                    letterSpacing: 'var(--tracking-display)',
                    color: 'var(--color-on-accent)',
                  }}
                >
                  {t('cookie.preferencesTitle', 'Preferenze Cookie')}
                </h2>
              </div>
              <button
                onClick={() => setShowPreferences(false)}
                className="p-2 -mr-1 transition-colors duration-[var(--duration-fast)] hover:bg-white/10"
                style={{ borderRadius: 'var(--radius-sm)' }}
                aria-label={t('aria.close', 'Chiudi')}
              >
                <X className="h-4 w-4 opacity-80" strokeWidth={1.5} />
              </button>
              <span
                aria-hidden="true"
                className="absolute left-0 right-0 bottom-0 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, var(--color-highlight) 20%, var(--color-highlight) 80%, transparent)',
                  opacity: 0.55,
                }}
              />
            </div>

            <div className="px-6 py-5" style={{ background: 'var(--color-bg)' }}>
              <p
                className="text-[0.875rem] mb-5"
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--color-fg)',
                  lineHeight: 1.6,
                }}
              >
                {t(
                  'cookie.preferencesDescription',
                  'Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.'
                )}
              </p>

              <div className="flex flex-col gap-px">
                <PreferenceRow
                  index="01"
                  title={t('cookie.necessary', 'Necessari')}
                  description={t(
                    'cookie.necessaryDesc',
                    'Essenziali per il funzionamento del sito.'
                  )}
                  control={
                    <span
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.625rem',
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: 'var(--color-highlight)',
                        fontWeight: 600,
                      }}
                    >
                      {t('cookie.alwaysOn', 'Sempre attivi')}
                    </span>
                  }
                />
                <PreferenceRow
                  index="02"
                  title={t('cookie.analytics', 'Analitici')}
                  description={t(
                    'cookie.analyticsDesc',
                    'Ci aiutano a capire come usi il sito.'
                  )}
                  control={
                    <Toggle
                      checked={tempPrefs.analytics}
                      onChange={() =>
                        setTempPrefs((p) => ({ ...p, analytics: !p.analytics }))
                      }
                      label={t('cookie.analytics', 'Analitici')}
                    />
                  }
                />
                <PreferenceRow
                  index="03"
                  title={t('cookie.marketing', 'Marketing')}
                  description={t(
                    'cookie.marketingDesc',
                    'Per contenuti e pubblicità personalizzati.'
                  )}
                  control={
                    <Toggle
                      checked={tempPrefs.marketing}
                      onChange={() =>
                        setTempPrefs((p) => ({ ...p, marketing: !p.marketing }))
                      }
                      label={t('cookie.marketing', 'Marketing')}
                    />
                  }
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={() => setShowPreferences(false)}
                  className={`h-11 px-6 text-sm font-medium border transition-[background,color,border] duration-[var(--duration-fast)] bg-transparent text-[var(--color-fg)] border-[var(--color-fg)] ${hoverChampagne}`}
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {t('cookie.cancel', 'Annulla')}
                </button>
                <button
                  onClick={() => {
                    savePreferences(tempPrefs)
                    setShowPreferences(false)
                  }}
                  className={`h-11 px-6 text-sm font-medium border transition-[background,color,border] duration-[var(--duration-fast)] bg-[var(--color-accent)] text-[var(--color-on-accent)] border-[var(--color-accent)] ${hoverChampagne} hover:border-[var(--color-fg)]`}
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {t('cookie.savePreferences', 'Salva preferenze')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function PreferenceRow({
  index,
  title,
  description,
  control,
}: {
  index: string
  title: string
  description: string
  control: React.ReactNode
}) {
  return (
    <div
      className="flex items-center justify-between gap-4 px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] -mt-px first:mt-0"
      style={{ borderRadius: 'var(--radius-sm)' }}
    >
      <div className="flex flex-col items-start min-w-0">
        <span
          aria-hidden="true"
          className="block mb-1"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.625rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--color-muted)',
            fontWeight: 500,
          }}
        >
          {index}
        </span>
        <p
          className="text-[0.9375rem] leading-tight"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            letterSpacing: 'var(--tracking-display)',
            color: 'var(--color-fg)',
          }}
        >
          {title}
        </p>
        <p
          className="text-[0.75rem] mt-1"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--color-muted)',
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      </div>
      <div className="flex-shrink-0">{control}</div>
    </div>
  )
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: () => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className="relative shrink-0 inline-flex h-5 w-10 items-center transition-colors duration-[var(--duration-fast)] border"
      style={{
        borderRadius: 'var(--radius-sm)',
        background: checked ? 'var(--color-accent)' : 'transparent',
        borderColor: checked ? 'var(--color-accent)' : 'var(--color-fg)',
      }}
    >
      <span
        className="inline-block h-3 w-3 transform transition-transform duration-[var(--duration-fast)]"
        style={{
          background: checked ? 'var(--color-highlight)' : 'var(--color-fg)',
          transform: checked ? 'translateX(22px)' : 'translateX(3px)',
        }}
      />
    </button>
  )
}
